import { Link, useLocation } from 'react-router-dom';

import { Breadcrumb } from '@/components/common/Breadcrumb';
import { QueryErrorView } from '@/components/common/QueryErrorView';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useGetOrganizationQuery } from '../api/orgApi';

export type OrgTab = 'projects' | 'members';

/**
 * Organization detail header (design §10/§11): the breadcrumb trail, the org
 * name, and the URL-synced Projects/Members tabs.
 *
 * Both route trees need it. The orgs layout route renders it above its
 * `<Outlet/>`; the projects feature renders the same component, because the flat
 * glob registry makes project routes siblings (not children) of the org layout —
 * so the tab strip and its 403 handling stay identical on both sides.
 *
 * A failed `GET /organizations/:id` replaces the frame: reads are enforced by the
 * data endpoint itself (design §14.5), and a denied org must not leave its tabs
 * on screen (403 → `PolicyDeniedView`).
 */
export function OrgDetailHeader({ orgId }: { orgId: string }) {
  const location = useLocation();
  const { data: organization, isLoading, isError, error, refetch } = useGetOrganizationQuery(orgId);

  if (isError) {
    return <QueryErrorView error={error} label="the organization" onRetry={() => void refetch()} />;
  }

  const name = organization?.name ?? '';
  const activeTab: OrgTab = location.pathname.endsWith('/members') ? 'members' : 'projects';

  return (
    <header className="mb-6 space-y-3">
      <Breadcrumb
        items={[
          { label: 'Home', to: '/' },
          { label: 'Organizations', to: '/organizations' },
          { label: isLoading || !organization ? '…' : name, to: null },
        ]}
      />

      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="min-w-0 truncate text-2xl font-semibold tracking-tight" title={name}>
          {isLoading ? '…' : name}
        </h1>

        <Tabs value={activeTab} aria-label="Organization sections">
          <TabsList>
            <TabsTrigger value="projects" asChild>
              <Link to={`/organizations/${orgId}/projects`}>Projects</Link>
            </TabsTrigger>
            <TabsTrigger value="members" asChild>
              <Link to={`/organizations/${orgId}/members`}>Members</Link>
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {organization?.description ? (
        <p className="text-sm text-muted-foreground">{organization.description}</p>
      ) : null}
    </header>
  );
}
